import { Trip } from "@workspace/domain/src/features/trip";
import { errorMessage, isInArray } from "@workspace/domain/src/utils";
import dayjs from "@workspace/domain/src/utils/dayjs";
import { FLAT_LOCATIONS } from "@workspace/domain/src/utils/locations";
import Decimal from "decimal.js";
import * as z from "zod";

const NonEditableFields = [
  "startDate" as const,
  "endDate" as const,
  "reservationDeadline" as const,
  "cancellationDeadline" as const,
];

const TripSchema = z.object({
  id: z.string(),

  serial: z.string(),

  // General information
  name: z.string(),
  description: z.any().nullable(),
  locations: z.array(
    z.object({
      type: z.enum(["CATALOG", "CUSTOM"]),
      locationId: z.string().nullable(),
      customName: z.string().nullable(),
    }),
  ),
  startDate: z.date(),
  endDate: z.date(),
  maxCapacity: z.number(),
  targetRevenue: z.number().nullable(),
  status: z.enum(Trip.Status),
  hotelName: z.string().nullable(),
  hotelLocation: z.string().nullable(),

  // Tags
  audience: z.enum(Trip.Audience),
  food: z.enum(Trip.Food),
  accessibility: z.array(z.enum(Trip.Accessibility)),
  transportation: z.array(z.enum(Trip.Transportation)),
  guides: z.array(z.enum(Trip.Guides)),

  // Images
  imageKeys: z.array(z.string()),

  // Pricing
  adultPrice: z.number(),
  childPrice: z.number(),
  childMaximumAge: z.number(),
  groupPricing: z.array(
    z.object({
      id: z.string(),
      adultPrice: z.number(),
      childPrice: z.number(),
      minimumNumberOfPeople: z.number(),
    }),
  ),

  // Reservation
  adultReservationFee: z.number(),
  childReservationFee: z.number(),
  reservationDeadline: z.date(),

  // Cancellation
  cancellationDeadline: z.date().nullable(),
  adultCancellationFee: z.number(),
  childCancellationFee: z.number(),

  // Visibility
  visible: z.boolean(),
  hasBeenPublished: z.boolean(),

  // Statistics
  currentReservations: z.number(),
  currentRevenue: z.number(),
  currentAmountDue: z.number(),

  // Relations
  agencyId: z.string(),

  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable(),
});

const AdultPriceSchema = z
  .number(errorMessage("Adult price is required", "Adult price is invalid"))
  .min(1, "Price must be greater than 0")
  .max(1_000_000, "Price must be less than 1,000,000")
  .transform((val) => new Decimal(val).times(100).toNumber());
const ChildPriceSchema = z
  .number("Child price is invalid")
  .min(0, "Child price must be at least 0")
  .max(1_000_000, "Child price must be less than 1,000,000")
  .transform((val) => new Decimal(val).times(100).toNumber());
const GroupPricingSchema = z.object({
  adultPrice: AdultPriceSchema,
  childPrice: ChildPriceSchema,
  minimumNumberOfPeople: z.number(
    errorMessage("Minimum number of people is required", "Minimum number of people is invalid"),
  ),
});

const SortBy = ["createdAt", "currentRevenue", "startDate", "currentReservations", "adultPrice"] as const;
const SortOrder = ["asc", "desc"] as const;

const GetSchema = z.object({
  id: z.string(),
});

const GetAllSchema = z
  .object({
    paginate: z
      .object({
        id: z.string(),
        cursor: z.union([z.number(), z.string(), z.date()]),
      })
      .optional(),
    limit: z.number(),
    clientId: z.string().optional(),
    discountCodeId: z.string().optional(),

    sortBy: z.enum(SortBy),
    sortOrder: z.enum(SortOrder),
  })
  .extend(
    z
      .object({
        search: z.string(),
        locations: z.array(z.string()),
        customLocationSearch: z.string(),
        status: z.array(z.enum(Trip.Status)),

        dateMin: z.union([z.iso.datetime().transform((date) => dayjs(date).toDate()), z.date()]),
        dateMax: z.union([z.iso.datetime().transform((date) => dayjs(date).toDate()), z.date()]),

        capacityMin: z.number(),
        capacityMax: z.number(),

        revenueMin: z.number().transform((val) => new Decimal(val).times(100).toNumber()),
        revenueMax: z.number().transform((val) => new Decimal(val).times(100).toNumber()),

        adultPriceMin: z.number().transform((val) => new Decimal(val).times(100).toNumber()),
        adultPriceMax: z.number().transform((val) => new Decimal(val).times(100).toNumber()),

        cancellationAllowed: z.boolean(),

        cancellationDeadlineMin: z.union([z.iso.datetime().transform((date) => dayjs(date).toDate()), z.date()]),
        cancellationDeadlineMax: z.union([z.iso.datetime().transform((date) => dayjs(date).toDate()), z.date()]),

        adultCancellationFeeMin: z.number().transform((val) => new Decimal(val).times(100).toNumber()),
        adultCancellationFeeMax: z.number().transform((val) => new Decimal(val).times(100).toNumber()),

        reservationDeadlineMin: z.union([z.iso.datetime().transform((date) => dayjs(date).toDate()), z.date()]),
        reservationDeadlineMax: z.union([z.iso.datetime().transform((date) => dayjs(date).toDate()), z.date()]),

        adultReservationFeeMin: z.number().transform((val) => new Decimal(val).times(100).toNumber()),
        adultReservationFeeMax: z.number().transform((val) => new Decimal(val).times(100).toNumber()),

        audience: z.array(z.enum(Trip.Audience)),
        food: z.array(z.enum(Trip.Food)),
        accessibility: z.array(z.enum(Trip.Accessibility)),
        transportation: z.array(z.enum(Trip.Transportation)),
        guides: z.array(z.enum(Trip.Guides)),

        visible: z.boolean().optional(),
      })
      .partial().shape,
  );

const BaseSchema = z
  .object({
    // General information
    name: z
      .string(errorMessage("Name is required", "Name is invalid"))
      .nonempty("Name is required")
      .max(255, "Name must be less than 255 characters"),
    description: z.any().optional(),
    locations: z
      .array(
        z.discriminatedUnion("type", [
          z.object({
            type: z.literal("CATALOG"),
            locationId: z.string(errorMessage("Location is required", "Location is invalid")).refine(
              (str) =>
                str.trim() === "" ||
                isInArray(
                  str,
                  FLAT_LOCATIONS.map((l) => l.id),
                ),
              "Location is invalid",
            ),
          }),
          z.object({
            type: z.literal("CUSTOM"),
            name: z
              .string(errorMessage("Location is required", "Location is invalid"))
              .trim()
              .min(1, "Location is required")
              .max(255, "Location must be less than 255 characters"),
          }),
        ]),
        errorMessage("Location is required", "Location is invalid"),
      )
      .min(1, "At least one location is required")
      .max(10, "At most 10 locations are allowed")
      .refine((arr) => arr.length > 0 && (arr[0]?.type !== "CATALOG" || arr[0].locationId.trim() !== ""), {
        path: ["0"],
        message: "Location is required",
      })
      .transform((arr) => arr.filter((location) => location.type !== "CATALOG" || location.locationId.trim() !== "")),

    startDate: z.date(errorMessage("Start date is required", "Start date is invalid")),
    endDate: z.date(errorMessage("End date is required", "End date is invalid")),
    maxCapacity: z
      .number(errorMessage("Max capacity is required", "Max capacity is invalid"))
      .min(1, "Max capacity must be greater than 0")
      .max(1_000, "Max capacity must be less than 1,000"),
    targetRevenue: z
      .number(errorMessage("Target revenue is required", "Target revenue is invalid"))
      .min(0, "Target revenue must be at least 0")
      .max(1_000_000, "Target revenue must be less than 1,000,000")
      .transform((val) => new Decimal(val).times(100).toNumber())
      .optional(),
    hotelName: z.string("Hotel name is invalid").optional(),
    hotelLocation: z
      .string("Hotel location is invalid")
      .regex(
        /^$|^(http(s?):\/\/)?((maps\.google\.com\/)|((www\.)?google\.com\/maps\/)|(goo\.gl\/maps\/)|(maps\.app\.goo\.gl\/)).*/,
        "Hotel location must be a Google Maps link",
      )
      .optional(),

    // Tags
    audience: z.enum(Trip.Audience, errorMessage("Audience is required", "Audience is invalid")),
    food: z.enum(Trip.Food, errorMessage("Food is required", "Food is invalid")),
    accessibility: z.array(
      z.enum(Trip.Accessibility, errorMessage("Accessibility is required", "Accessibility is invalid")),
    ),
    transportation: z.array(
      z.enum(Trip.Transportation, errorMessage("Transportation is required", "Transportation is invalid")),
    ),
    guides: z.array(z.enum(Trip.Guides, errorMessage("Guides is required", "Guides is invalid"))),

    childPricing: z.boolean(errorMessage("Child pricing is required", "Child pricing is invalid")),

    // Pricing
    adultPrice: AdultPriceSchema,
    childPrice: ChildPriceSchema,
    childMaximumAge: z
      .number("Child maximum age is invalid")
      .min(0, "Child maximum age must be at least 0")
      .max(18, "Child maximum age must be less than 18"),
    groupPricing: z.array(GroupPricingSchema, "Group pricing is invalid").optional(),

    // Reservation
    adultReservationFee: z
      .number(errorMessage("Reservation fee is required", "Reservation fee is invalid"))
      .min(0, "Reservation fee must be at least 0")
      .max(1_000_000, "Reservation fee must be less than 1,000,000")
      .transform((val) => new Decimal(val).times(100).toNumber()),
    childReservationFee: z
      .number("Child reservation fee is invalid")
      .min(0, "Child reservation fee must be at least 0")
      .max(1_000_000, "Child reservation fee must be less than 1,000,000")
      .transform((val) => new Decimal(val).times(100).toNumber()),
    reservationDeadline: z.date(errorMessage("Reservation deadline is required", "Reservation deadline is invalid")),

    // Cancellation
    cancellationDeadline: z.date("Cancellation deadline is invalid").optional(),
    adultCancellationFee: z
      .number("Cancellation fee is invalid")
      .min(0, "Cancellation fee must be at least 0")
      .max(1_000_000, "Cancellation fee must be less than 1,000,000")
      .transform((val) => new Decimal(val).times(100).toNumber())
      .optional(),
    childCancellationFee: z
      .number("Child cancellation fee is invalid")
      .min(0, "Child cancellation fee must be at least 0")
      .max(1_000_000, "Child cancellation fee must be less than 1,000,000")
      .transform((val) => new Decimal(val).times(100).toNumber())
      .optional(),

    // Visibility
    visible: z.boolean().optional(),
  })
  .refine(
    (data) =>
      !data.childPricing ||
      !data.cancellationDeadline ||
      !data.childCancellationFee ||
      data.childCancellationFee <=
        Math.min(data.childPrice, ...(data.groupPricing?.map((group) => group.childPrice) ?? [])),
    {
      path: ["childCancellationFee"],
      message: "Child cancellation fee must be less than the lowest child price",
    },
  )
  .refine(
    (data) =>
      !data.childPricing ||
      data.childReservationFee <=
        Math.min(data.childPrice, ...(data.groupPricing?.map((group) => group.childPrice) ?? [])),
    {
      path: ["childReservationFee"],
      message: "Child reservation fee must be less than the lowest child price",
    },
  )
  .refine(
    (data) =>
      !data.cancellationDeadline ||
      !data.adultCancellationFee ||
      data.adultCancellationFee <=
        Math.min(data.adultPrice, ...(data.groupPricing?.map((group) => group.adultPrice) ?? [])),
    {
      path: ["adultCancellationFee"],
      message: "Adult cancellation fee must be less than the lowest adult price",
    },
  )
  .refine(
    (data) =>
      data.adultReservationFee <=
      Math.min(data.adultPrice, ...(data.groupPricing?.map((group) => group.adultPrice) ?? [])),
    {
      path: ["adultReservationFee"],
      message: "Adult reservation fee must be less than the lowest adult price",
    },
  )
  .refine((data) => !data.childPricing || data.childMaximumAge > 0, {
    path: ["childMaximumAge"],
    message: "Child maximum age is required when child pricing is enabled",
  })
  .refine((data) => !dayjs(data.startDate).isBefore(data.reservationDeadline), {
    path: ["reservationDeadline"],
    message: "Reservation deadline must be before start date",
  })
  .refine((data) => !data.cancellationDeadline || !dayjs(data.startDate).isBefore(data.cancellationDeadline), {
    path: ["cancellationDeadline"],
    message: "Cancellation deadline must be before start date",
  })
  .refine((data) => !dayjs(data.endDate).isBefore(data.startDate), {
    path: ["endDate"],
    message: "End date must be after start date",
  })
  // Child age and price
  .refine((data) => (data.childPrice !== undefined && data.childMaximumAge === undefined ? false : true), {
    path: ["childMaximumAge"],
    message: "Child maximum age is required when child price is provided",
  })
  .refine((data) => (data.childPrice === undefined && data.childMaximumAge !== undefined ? false : true), {
    path: ["childPrice"],
    message: "Child price is required when child maximum age is provided",
  });

const CreateSchema = BaseSchema
  // After now dates (checked on new only)
  .refine((data) => !dayjs(data.startDate).isBefore(dayjs()), {
    path: ["startDate"],
    message: "Start date must be in the future",
  })
  .refine((data) => !dayjs(data.endDate).isBefore(dayjs()), {
    path: ["endDate"],
    message: "End date must be in the future",
  })
  .refine((data) => !dayjs(data.cancellationDeadline).isBefore(dayjs()), {
    path: ["cancellationDeadline"],
    message: "Cancellation deadline must be in the future",
  })
  .refine((data) => !dayjs(data.reservationDeadline).isBefore(dayjs()), {
    path: ["reservationDeadline"],
    message: "Reservation deadline must be in the future",
  });

const UpdateSchema = BaseSchema.safeExtend({
  ...GetSchema.shape,
  existingImageKeys: z.array(z.string()).optional(),
  newImages: z
    .array(
      z
        .instanceof(File)
        .refine((file) => ["image/jpeg", "image/png", "image/webp"].includes(file.type), {
          message: "Image must be a JPEG, PNG, or WebP",
        })
        .refine((file) => file.size <= 10 * 1024 ** 2, "Image must be less than 10MB"),
    )
    .optional(),
});

const DeleteSchema = GetSchema;

export const AdminTrip = {
  SortBy: SortBy,
  SortOrder: SortOrder,
  NonEditableFields: NonEditableFields,
  Trip: TripSchema,
  Get: GetSchema,
  GetAll: GetAllSchema,
  Create: CreateSchema,
  Update: UpdateSchema,
  Delete: DeleteSchema,
};
export type AdminTrip = {
  SortBy: (typeof SortBy)[number];
  SortOrder: (typeof SortOrder)[number];
  NonEditableFields: typeof NonEditableFields;
  Trip: z.infer<typeof TripSchema>;
  Get: z.infer<typeof GetSchema>;
  GetAll: z.infer<typeof GetAllSchema>;
  Create: z.infer<typeof CreateSchema>;
  Update: z.infer<typeof UpdateSchema>;
  Delete: z.infer<typeof DeleteSchema>;
};
